import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, PanelRightOpen } from 'lucide-react';
import { SetupStep, submitForwarding, downloadForwardingGuide } from '../../../services/accountSetup/accountSetupService';
import { borderGrey, canvasBg, secondaryText } from '../../../styling/stylingHelper';
import { setupButton, setupInput, setupSecondary, SetupError, setupErrorKey } from './SetupPrimitives';
import SetupForwardingGuide from './SetupForwardingGuide';

export const FORWARDING_PROVIDERS = ['outlook', 'gmail', 'hostnet', 'mijndomein'] as const;
export default function SetupForwarding({ step, onChange }: { step: SetupStep; onChange: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [provider, setProvider] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const base = 'accountSetup.forwarding';
  const ready = !!step.forwardingAlias && step.cloudStatus !== 'not_applicable';
  const verified = step.customerDone && step.cloudStatus === 'done';
  const hasGuide = FORWARDING_PROVIDERS.some(value => value === provider);
  const language = (i18n.resolvedLanguage || i18n.language).startsWith('nl') ? 'nl' : 'en';
  const download = async () => {
    setBusy(true); setError('');
    try { await downloadForwardingGuide(step, provider, language); }
    catch (failure) { setError(setupErrorKey(failure)); await onChange().catch(() => undefined); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true); setError('');
    try { await submitForwarding(step); await onChange(); setOpened(false); }
    catch (failure) { setError(setupErrorKey(failure)); await onChange().catch(() => undefined); }
    finally { setBusy(false); }
  };
  const completion = <div className="space-y-3">
    <p className={`text-sm ${secondaryText}`}>{t(`${base}.${verified ? 'verified' : step.customerDone ? 'submitted' : 'completionHelp'}`)}</p>
    {!verified && <button className={setupButton} disabled={busy || step.customerDone} onClick={() => void submit()}>{t(`${base}.complete`)}</button>}
    <SetupError message={error} />
  </div>;
  return <div className="space-y-5">
    <div className={`rounded-xl p-4 ${canvasBg}`}>
      <h3 className="font-semibold">{t(`${base}.aliasTitle`)}</h3>
      {!step.forwardingAlias && <p className={`mt-2 text-sm leading-6 ${secondaryText}`}>{t(`${base}.aliasWaiting`)}</p>}
      {step.forwardingAlias && <label className="mt-3 block text-sm">{t(`${base}.aliasLabel`)}<input className={`${setupInput} mt-1`} readOnly value={step.forwardingAlias} onFocus={event => event.target.select()} /></label>}
    </div>
    <h3 className="font-semibold">{t(`${base}.choiceTitle`)}</h3>
    <div className={`space-y-4 rounded-xl border p-4 ${borderGrey}`}>
      <h4 className="font-medium">{t(`${base}.guideAction`)}</h4>
      <label className="block text-sm font-medium">{t(`${base}.provider`)}<select className={`${setupInput} mt-2`} value={provider} disabled={!ready || busy} onChange={event => setProvider(event.target.value)}>
        <option value="">{t(`${base}.chooseProvider`)}</option>
        {FORWARDING_PROVIDERS.map(value => <option key={value} value={value}>{t(`${base}.guides.${value}.name`)}</option>)}
        <option value="other">{t(`${base}.other`)}</option>
      </select></label>
      <p className={`text-sm leading-6 ${secondaryText}`}>{t(`${base}.${provider === 'other' ? 'otherHelp' : 'providerHelp'}`)}</p>
      <div className="flex flex-wrap gap-3">
        <button className={setupSecondary} disabled={!ready || !hasGuide || busy} onClick={() => void download()}><Download className="h-4 w-4" />{t(`${base}.download`)}</button>
        <button className={setupSecondary} disabled={!ready || !hasGuide} onClick={() => setOpened(true)}><PanelRightOpen className="h-4 w-4" />{t(`${base}.followHere`)}</button>
      </div>
    </div>
    {ready ? completion : <SetupError message={error} />}
    <div className={`flex flex-wrap items-center gap-3 border-t pt-4 ${borderGrey}`}>
      <p className={`text-sm ${secondaryText}`}>{t(`${base}.needSupport`)}</p>
      <button className={setupSecondary} disabled>{t(`${base}.meetingAction`)} · {t('accountSetup.comingSoon')}</button>
    </div>
    {opened && ready && hasGuide && <SetupForwardingGuide provider={provider} alias={step.forwardingAlias!} onClose={() => setOpened(false)} footer={completion} />}
  </div>;
}
